import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { downloadUrl } from '@/lib/download'
import { cn } from '@/lib/utils'
import { LoadingOverlay } from './LoadingOverlay'

export type ExportFormat = 'xlsx' | 'csv'

export interface ListExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void

  /**
   * Entity label used to build the download filename:
   * `<entityLabel>_list_[YYYY-MM-DD].<ext>` (e.g. "Clients_list_2026-06-18.xlsx").
   */
  entityLabel: string

  /** Number of rows in scope; export is blocked (with a toast) when 0. */
  rowCount?: number

  /**
   * Hits the caller's export endpoint for the chosen format and returns the
   * resulting file URL (and optional server-provided filename). The dialog
   * triggers the browser download and closes on success.
   */
  onExport: (
    format: ExportFormat
  ) => Promise<{ url: string; filename?: string }>

  /** Optional title override (e.g. module-specific i18n). */
  title?: string

  /** Optional scope note override (e.g. module-specific i18n). */
  scopeNote?: string
}

const FORMATS: ExportFormat[] = ['xlsx', 'csv']

// Figma 953:59782 — unselected grey ring / selected emerald fill + white dot.
function FormatRadio({ selected }: { selected: boolean }) {
  return (
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-full border bg-white',
        selected ? 'border-transparent bg-[#10b981]' : 'border-[#b0b5c3]'
      )}
    >
      {selected && <span className='size-3 rounded-full bg-white' />}
    </span>
  )
}

/** Build `<Entity>_list_[YYYY-MM-DD].<ext>`. */
// eslint-disable-next-line react-refresh/only-export-components
export function buildExportFilename(
  entityLabel: string,
  format: ExportFormat,
  date: Date = new Date()
): string {
  const iso = date.toISOString().slice(0, 10)
  return `${entityLabel}_list_${iso}.${format}`
}

// Figma 953:59782 — shared list export: pick .xlsx / .csv, download with standard filename.
export function ListExportDialog({
  open,
  onOpenChange,
  entityLabel,
  rowCount,
  onExport,
  title,
  scopeNote,
}: ListExportDialogProps) {
  const { t } = useTranslation(['shared', 'common'])
  const [format, setFormat] = useState<ExportFormat>('csv')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (rowCount === 0) {
      toast({ variant: 'destructive', title: t('shared:exportDialog.empty') })
      return
    }
    setBusy(true)
    try {
      const { url, filename } = await onExport(format)
      downloadUrl(url, filename ?? buildExportFilename(entityLabel, format))
      toast({ variant: 'success', title: t('shared:exportDialog.success') })
      onOpenChange(false)
    } catch {
      toast({ variant: 'destructive', title: t('shared:exportDialog.error') })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            'max-w-[600px] gap-0 border-[#e7e9ef] bg-white p-8 shadow-[0px_1px_2px_rgba(38,45,66,0.05),0px_8px_8px_rgba(38,45,66,0.1)] sm:rounded-2xl'
          )}
        >
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2 pr-8'>
              <DialogTitle className='text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
                {title ?? t('shared:exportDialog.title')}
              </DialogTitle>
              <p className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {scopeNote ?? t('shared:exportDialog.scopeNote')}
              </p>
            </div>

            <div className='flex gap-4'>
              {FORMATS.map(f => {
                const selected = format === f
                return (
                  <button
                    key={f}
                    type='button'
                    onClick={() => setFormat(f)}
                    className={cn(
                      'flex min-w-[240px] flex-1 rounded-xl p-3.5 transition-colors',
                      selected
                        ? 'border-2 border-[#059669] bg-[#ecfdf5]'
                        : 'border border-[#e7e9ef] bg-white hover:bg-[#f4f5f8]'
                    )}
                  >
                    <div className='flex w-full items-center gap-4'>
                      <span className='flex-1 text-left text-sm font-medium leading-[1.4] text-[#171a20]'>
                        .{f}
                      </span>
                      <FormatRadio selected={selected} />
                    </div>
                  </button>
                )
              })}
            </div>

            <div className='flex items-center justify-end gap-4'>
              <Button
                variant='outline'
                className='h-auto rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f4f5f8]'
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                {t('common:actions.cancel')}
              </Button>
              <Button
                className='h-auto rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'
                onClick={submit}
                disabled={busy}
              >
                {t('shared:exportDialog.export')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <LoadingOverlay open={busy} />
    </>
  )
}

export default ListExportDialog
